<script lang="ts" setup>
import CandidateDropdown from '@/core/components/form-elements/CandidateDropdown.vue';
import CustomerDropdown from '@/core/components/form-elements/CustomerDropdown.vue';
import { useFollowUpFiltersSchema } from '@/modules/follow-up/schemas/follow-up-filters.schema';
import { ApiResource, Candidate, CANDIDATE_ENDPOINTS, CaseFileType, ProgressionState, useYupValidation } from '@nxtpeople/nxt-styles';
import { deepEqual } from '@nxtpeople/nxt-vue-library';
import { watchDebounced } from '@vueuse/core';

//#region Props & Emits
const props = defineProps<{
   progressionStates: Array<ProgressionState>;
   caseFileTypes: Array<CaseFileType>;
}>();
const emits = defineEmits<{
   (e: 'resetFilters'): void;
}>();

const query = defineModel<Record<string, any>>('query', { required: true });
//#endregion

//#region Filters
const { FOLLOW_UP_FILTERS_SCHEMA } = useFollowUpFiltersSchema();
const filters = ref({ ...query.value });
const { errors, validate } = useYupValidation({ data: filters, schema: FOLLOW_UP_FILTERS_SCHEMA });

function formatTags(tags: Array<{ name: string }>) {
   return tags.map((tag) => tag.name).join(', ');
}

function queryChanged(newQuery: Record<string, string>) {
   if (deepEqual(newQuery, filters.value)) return;
   filters.value = { ...newQuery };
}

function filtersChanged() {
   validate();

   if (deepEqual(filters.value, query.value)) return;
   query.value = { ...filters.value };
}

watch(query, queryChanged, { immediate: true });
watchDebounced(filters, filtersChanged, { debounce: 300, deep: true });
//#endregion

//#region Case File Types
const filteredCaseFileTypes = computed({
   get: () =>
      (props.caseFileTypes ?? []).filter((caseFileType) => (filters.value.case_file_type_ids ?? []).includes(caseFileType.id.toString())) ??
      [],
   set: (newValue: Array<CaseFileType>) => {
      filters.value.case_file_type_ids = newValue.map((caseFileType) => caseFileType.id.toString());
   },
});
//#endregion

//#region Progression States
const filteredProgressionStates = computed({
   get: () =>
      (props.progressionStates ?? []).filter((progressionState) =>
         (filters.value.progression_state_ids ?? []).includes(progressionState.id.toString()),
      ) ?? [],
   set: (newValue: Array<ProgressionState>) => {
      filters.value.progression_state_ids = newValue.map((progressionState) => progressionState.id.toString());
   },
});
//#endregion

//#region Show My Case Files
const showMyCaseFiles = computed({
   get: () => filters.value.show_my_case_files == '1',
   set: (newValue: boolean) => {
      filters.value.show_my_case_files = newValue ? '1' : '0';
   },
});
//#endregion

//#region Customer
const customerId = computed({
   get: () => ((filters.value.customer_ids ?? []).length ? filters.value.customer_ids[0] : null),
   set: (newValue: number | null) => {
      filters.value.customer_ids = newValue ? [newValue] : [];
   },
});
//#endregion

//#region Candidate
async function getCandidate() {
   if (!query.value.candidate_sync_id) {
      return;
   }

   const response = await http.get<ApiResource<Candidate>>(CANDIDATE_ENDPOINTS.GET(query.value.candidate_sync_id));
   return response.data.data;
}
//#endregion

//#region More filters count
const moreFiltersCount = computed(() => {
   let count = 0;

   if (filters.value.show_my_case_files == '1') {
      count += 1;
   }

   if (filters.value.from) {
      count += 1;
   }

   if (filters.value.till) {
      count += 1;
   }

   return count;
});
//#endregion
</script>

<template>
   <NxtGenericFilters
      v-model:filters="filters"
      :more-filters-count="moreFiltersCount"
      class="follow-up-filters"
      @on-reset-filters="emits('resetFilters')"
   >
      <template #main-filters>
         <div class="follow-up-filters__main">
            <CandidateDropdown
               :candidate-sync-id="filters?.candidate_sync_id"
               :name="'filter_candidate_sync_id'"
               :placeholder="$t('employees.employee')"
               :initial-data-callback="getCandidate"
               :employer-id="query?.employer_id"
               @update:candidate-sync-id="filters.candidate_sync_id = $event"
            />

            <NxtMultiSelect
               v-model="filteredCaseFileTypes"
               :name="'filter-case_file_types'"
               :options="caseFileTypes"
               :placeholder="$t('case-files.case-file-type', 2)"
            >
               <template #tags="{ modelValue }">
                  <div class="follow-up-filters__multi-select">
                     {{ formatTags(modelValue) }}
                  </div>
               </template>
            </NxtMultiSelect>

            <NxtMultiSelect
               v-model="filteredProgressionStates"
               :name="'filter-progression_states'"
               :options="progressionStates"
               :placeholder="$t('generic.status.status', 2)"
            >
               <template #tags="{ modelValue }">
                  <div class="follow-up-filters__multi-select">
                     {{ formatTags(modelValue) }}
                  </div>
               </template>
            </NxtMultiSelect>

            <CustomerDropdown v-model:customer-id="customerId" :name="'filter-customer'" :placeholder="$t('customers.customer', 1)" />
         </div>
      </template>

      <template #right-filters>
         <div class="follow-up-filters__right-filters">
            <NxtToggle
               v-model="showMyCaseFiles"
               :name="'filter-show_my_case_files'"
               :label="$t('nxt-candidates.case-files.only-show-my-case-files')"
               :flip="true"
            />

            <NxtDatepicker v-model="filters.from" :name="'from'" :label="$t('generic.dates.from')" :errors="errors" />

            <NxtDatepicker v-model="filters.till" :name="'till'" :label="$t('generic.dates.till')" :errors="errors" />

            <NxtIconButton :name="'undo'" :hover-text="$t('actions.reset-filters')" @on-click="emits('resetFilters')" />
         </div>
      </template>
   </NxtGenericFilters>
</template>

<style lang="scss" scoped>
.follow-up-filters {
   background-color: var(--surface-base-0);
   width: 100%;

   &:deep(.nxt-multi-select-main__content) {
      overflow: hidden;
   }

   &:deep(.nxt-slide-out-panel__panel),
   &:deep(.nxt-drawer) {
      z-index: var(--zindex-fixed);
   }
}

.follow-up-filters__main {
   display: flex;
   align-items: center;
   flex-flow: row nowrap;
   gap: var(--nxt-gutter-small);
   flex: 1 1 0%;
   min-width: 0;
   max-width: 100%;
   overflow: hidden;

   :deep(.nxt-form-group) {
      flex: 1 1 0;
      min-width: 0;
      max-width: none;

      > * {
         width: 100%;
      }
   }
}

.follow-up-filters__right-filters {
   display: flex;
   align-items: end;
   justify-content: flex-end;
   gap: var(--nxt-gutter-small);
   flex: 0 0 auto;
   flex-shrink: 0;

   > * {
      &:first-child {
         white-space: nowrap;
         margin-bottom: var(--nxt-gutter-small);
      }

      &:nth-child(2),
      &:nth-child(3) {
         max-width: toRem(150);
      }
   }
}

.follow-up-filters__multi-select {
   max-width: toRem(150);
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   max-height: var(--nxt-input-height);
}
</style>
